import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, DatePicker, Popconfirm, Select, Space, Statistic, Table, Tag, message } from "antd";
import { ReloadOutlined, DeleteOutlined } from "@ant-design/icons";
import { getData, getErrorMessage, isSuccess, rfidAdminApi } from "../../hooks/rfidAdminApi";
import "./RfidAdmin.css";

const { RangePicker } = DatePicker;

const statusColor = {
  AUTHORIZED: "green",
  UNREGISTERED_TAG: "red",
  UNASSIGNED_TAG: "orange",
  INACTIVE_TAG: "volcano",
  INACTIVE_STUDENT: "purple",
};

const defaultTotalSummary = {
  total_count: 0,
  authorized_count: 0,
  unregistered_tag_count: 0,
  unassigned_tag_count: 0,
  inactive_tag_count: 0,
  inactive_student_count: 0,
};

const formatMeter = (value) => {
  const number = Number(value || 0);
  return Number.isInteger(number) ? number.toLocaleString() : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const RecordAdmin = ({ auth }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totalSummary, setTotalSummary] = useState(defaultTotalSummary);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [status, setStatus] = useState(undefined);

  const unitLength = Number(auth?.length || 0);

  const selectedClass = useMemo(
    () => classes.find((item) => String(item.id) === String(selectedClassId)),
    [classes, selectedClassId]
  );

  const authorizedLogsCount = useMemo(
    () => logs.filter((item) => item.status === "AUTHORIZED").length,
    [logs]
  );

  const totalAuthorizedCount = Number(totalSummary.authorized_count || 0);
  const totalMeters = totalAuthorizedCount * unitLength;
  const currentMeters = authorizedLogsCount * unitLength;

  const buildParams = () => {
    const params = {};
    if (status) params.status = status;
    if (dateRange?.[0]) params.dateFrom = dateRange[0].format("YYYY-MM-DD");
    if (dateRange?.[1]) params.dateTo = dateRange[1].format("YYYY-MM-DD");
    return params;
  };

  const loadClasses = async () => {
    try {
      const res = await rfidAdminApi.getClasses();
      const rows = getData(res);
      setClasses(rows);
      // Keep the initial view unfiltered so unregistered/unassigned RFID reads are visible.
    } catch (error) {
      messageApi.error(getErrorMessage(error, "반 목록을 불러오지 못했습니다."));
    }
  };

  const loadStudents = async (classId = selectedClassId) => {
    if (!classId) {
      setStudents([]);
      return;
    }
    try {
      const res = await rfidAdminApi.getStudents(classId);
      setStudents(getData(res));
    } catch (error) {
      messageApi.error(getErrorMessage(error, "학생 목록을 불러오지 못했습니다."));
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const [logRes, summaryRes, totalSummaryRes] = await Promise.all([
        selectedStudentId
          ? rfidAdminApi.getStudentEvents(selectedStudentId, params)
          : selectedClassId
            ? rfidAdminApi.getClassEvents(selectedClassId, params)
            : rfidAdminApi.getEvents(params),
        selectedClassId ? rfidAdminApi.getClassSummary(selectedClassId, params) : Promise.resolve({ status: true, data: [] }),
        rfidAdminApi.getTotalSummary(params).catch(() => ({ status: true, data: defaultTotalSummary })),
      ]);
      setLogs(getData(logRes));
      setSummary(getData(summaryRes));
      if (isSuccess(totalSummaryRes)) {
        setTotalSummary({ ...defaultTotalSummary, ...(totalSummaryRes.data || {}) });
      }
    } catch (error) {
      messageApi.error(getErrorMessage(error, "기록을 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedStudentId(null);
    loadStudents(selectedClassId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId]);

  useEffect(() => {
    loadRecords();
    const timer = setInterval(loadRecords, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, selectedStudentId, status]);

  const handleReset = async () => {
    try {
      const res = await rfidAdminApi.resetAll();
      if (!isSuccess(res)) throw new Error(res?.error || "초기화 실패");
      messageApi.success("전체 데이터가 초기화되었습니다.");
      setSelectedClassId(null);
      setSelectedStudentId(null);
      setLogs([]);
      setSummary([]);
      setTotalSummary(defaultTotalSummary);
      await loadClasses();
    } catch (error) {
      messageApi.error(getErrorMessage(error, "초기화 중 오류가 발생했습니다."));
    }
  };

  const logColumns = [
    { title: "시간", dataIndex: "read_at", width: 180, render: (v) => v ? new Date(v).toLocaleString() : "-" },
    { title: "상태", dataIndex: "status", width: 150, render: (v) => <Tag color={statusColor[v] || "default"}>{v}</Tag> },
    { title: "반", dataIndex: "class_name", width: 120, render: (v) => v || "-" },
    { title: "번호", dataIndex: "student_no", width: 80, render: (v) => v || "-" },
    { title: "학생", dataIndex: "student_name", width: 120, render: (v) => v || "-" },
    { title: "태그", dataIndex: "tag_label", width: 120, render: (v) => v || "-" },
    { title: "EPC", dataIndex: "epc", width: 230, render: (v) => <code>{v}</code> },
    { title: "리더", dataIndex: "reader_name", width: 130, render: (v) => v || "-" },
    { title: "위치", dataIndex: "reader_location", width: 150, render: (v) => v || "-" },
    { title: "ANT", dataIndex: "antenna_port", width: 70, render: (v) => v ?? "-" },
    { title: "RSSI", dataIndex: "rssi", width: 80, render: (v) => v ?? "-" },
  ];

  const summaryColumns = [
    { title: "번호", dataIndex: "student_no", width: 80, render: (v) => v || "-" },
    { title: "학생", dataIndex: "student_name" },
    { title: "기록 수", dataIndex: "total_reads", width: 100 },
    {
      title: `거리(m) · 1회 ${unitLength}m`,
      dataIndex: "total_reads",
      width: 150,
      render: (v) => `${formatMeter(Number(v || 0) * unitLength)}m`,
    },
    { title: "마지막 기록", dataIndex: "last_read_at", width: 180, render: (v) => v ? new Date(v).toLocaleString() : "-" },
  ];

  return (
    <div className="rfid-page">
      {contextHolder}
      <div className="rfid-page-header">
        <div>
          <h1 className="rfid-page-title">기록 조회</h1>
          <p className="rfid-page-desc">반별/학생별 UHF RFID 판독 기록과 요약을 확인합니다. 로그인 응답의 length 값으로 거리(m)를 계산합니다.</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadRecords}>조회</Button>
          <Popconfirm
            title="전체 초기화"
            description="반, 학생, 태그, 배정, 기록 전체를 삭제합니다. 정말 초기화할까요?"
            okText="초기화"
            cancelText="취소"
            onConfirm={handleReset}
          >
            <Button danger icon={<DeleteOutlined />}>전체 초기화</Button>
          </Popconfirm>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        message="실시간 전체 판독은 반 선택을 비운 상태에서 확인하세요."
        description="미등록 태그와 미배정 태그에는 반 정보가 없으므로 반을 선택하면 목록에서 제외됩니다. 이 화면은 3초마다 자동 갱신됩니다."
        style={{ marginBottom: 16 }}
      />

      <Card title="조회 조건">
        <div className="rfid-toolbar">
          <Select
            style={{ width: 220 }}
            allowClear
            placeholder="반 선택"
            value={selectedClassId || undefined}
            onChange={setSelectedClassId}
            options={classes.map((item) => ({ value: item.id, label: `${item.school_year} ${item.name}` }))}
          />
          <Select
            style={{ width: 220 }}
            allowClear
            placeholder="학생 선택"
            value={selectedStudentId || undefined}
            onChange={setSelectedStudentId}
            options={students.map((item) => ({ value: item.id, label: `${item.student_no ? `${item.student_no}번 ` : ""}${item.name}` }))}
            disabled={!selectedClassId}
          />
          <Select
            style={{ width: 190 }}
            allowClear
            placeholder="상태"
            value={status}
            onChange={setStatus}
            options={Object.keys(statusColor).map((key) => ({ value: key, label: key }))}
          />
          <RangePicker value={dateRange} onChange={setDateRange} />
          <Button type="primary" onClick={loadRecords}>검색</Button>
        </div>
      </Card>

      <div className="rfid-section-gap rfid-grid-3">
        <Card>
          <Statistic title="조회 기록 수" value={logs.length} />
        </Card>
        <Card>
          <Statistic title="조회 승인 기록 수" value={authorizedLogsCount} />
        </Card>
        <Card>
          <Statistic title="조회 거리" value={formatMeter(currentMeters)} suffix="m" />
        </Card>
      </div>

      <div className="rfid-section-gap rfid-grid-3">
        <Card>
          <Statistic title="전체 반 총 기록 수" value={Number(totalSummary.total_count || 0)} />
        </Card>
        <Card>
          <Statistic title="전체 반 승인 기록 수" value={totalAuthorizedCount} />
        </Card>
        <Card>
          <Statistic title="전체 반 누적 거리" value={formatMeter(totalMeters)} suffix="m" />
        </Card>
      </div>

      <div className="rfid-section-gap rfid-grid-3">
        <Card>
          <Statistic title="반 요약 학생 수" value={summary.length} />
        </Card>
        <Card>
          <Statistic title="선택 반" value={selectedClass ? selectedClass.name : "전체/미선택"} />
        </Card>
        <Card>
          <Statistic title="1회 길이" value={formatMeter(unitLength)} suffix="m" />
        </Card>
      </div>

      <Card className="rfid-section-gap" title="반별 학생 요약">
        <Table
          rowKey={(record) => record.student_id || record.student_name}
          loading={loading}
          columns={summaryColumns}
          dataSource={summary}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Card className="rfid-section-gap" title="RFID 판독 기록">
        <Table
          rowKey="id"
          loading={loading}
          columns={logColumns}
          dataSource={logs}
          pagination={{ pageSize: 15 }}
          scroll={{ x: 1450 }}
        />
      </Card>
    </div>
  );
};

export default RecordAdmin;
